import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _Icon, _Icon2;
var _excluded = ["AvatarProps", "buttonText", "CustomTrigger", "icon", "onClick", "toggleCustomTriggerOpenOnClick", "triggerType", "variant"],
  _excluded2 = ["sx"];
import React, { useState, useRef } from 'react';
import { Button } from '../Button';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import { MenuHeader } from './internal/MenuHeader';
import { DropDownMenuItems } from './internal/DropDownMenuItem';
import Avatar from '@mui/material/Avatar';
import { SHADOWS } from '@im/im-theme/constants';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import { Icon } from '../Icon';
import { ERROR100, ERROR50, ERROR700, GRAY100, PRIMARY100, PRIMARY50, PRIMARY700 } from '../common/colorConstants';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export function DropdownMenu(props) {
  var anchorEl = useRef(null);
  var _useState = useState((props == null ? void 0 : props.open) || false),
    _useState2 = _slicedToArray(_useState, 2),
    openMenu = _useState2[0],
    setOpenMenu = _useState2[1];
  var theme = useTheme();
  var controlledAnchorEl = props.anchorEl,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    inputRef = props.inputRef,
    menuHeaderProps = props.menuHeaderProps,
    menuItemProps = props.menuItemProps,
    menuMaxHeight = props.menuMaxHeight,
    menuMaxWidth = props.menuMaxWidth,
    menuTriggerProps = props.menuTriggerProps,
    popperProps = props.popperProps,
    _props$menuPaperProps = props.menuPaperProps,
    menuPaperProps = _props$menuPaperProps === void 0 ? {} : _props$menuPaperProps;
  var AvatarProps = menuTriggerProps.AvatarProps,
    buttonText = menuTriggerProps.buttonText,
    CustomTrigger = menuTriggerProps.CustomTrigger,
    icon = menuTriggerProps.icon,
    onClick = menuTriggerProps.onClick,
    toggleCustomTriggerOpenOnClick = menuTriggerProps.toggleCustomTriggerOpenOnClick,
    _menuTriggerProps$tri = menuTriggerProps.triggerType,
    triggerType = _menuTriggerProps$tri === void 0 ? 'Button' : _menuTriggerProps$tri,
    _menuTriggerProps$var = menuTriggerProps.variant,
    variant = _menuTriggerProps$var === void 0 ? triggerType === 'Icon' ? 'tertiary-gray' : 'secondary-gray' : _menuTriggerProps$var,
    otherMenuTriggerProps = _objectWithoutProperties(menuTriggerProps, _excluded);
  var paperSx = menuPaperProps.sx,
    otherPaperProps = _objectWithoutProperties(menuPaperProps, _excluded2);
  var handleMenuOpen = function handleMenuOpen() {
    var _inputRef$current;
    if (disabled) return;
    if (toggleCustomTriggerOpenOnClick) {
      setOpenMenu(!openMenu);
      onClick && onClick(!openMenu);
    } else {
      setOpenMenu(true);
      onClick && onClick(true);
    }
    inputRef && ((_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.classList.add('Mui-focused'));
  };
  var handleMenuClose = function handleMenuClose(event) {
    var _inputRef$current2;
    if (disabled) return;
    setOpenMenu(false);
    onClick && onClick(false);
    inputRef && ((_inputRef$current2 = inputRef.current) == null ? void 0 : _inputRef$current2.classList.remove('Mui-focused'));
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [triggerType === 'Custom' && CustomTrigger && /*#__PURE__*/_jsx(CustomTrigger, {
      onTriggerClick: handleMenuOpen,
      open: openMenu
    }), triggerType === 'Avatar' && /*#__PURE__*/_jsx(IconButton, _extends({
      disabled: disabled,
      onClick: handleMenuOpen
    }, otherMenuTriggerProps, {
      ref: anchorEl,
      sx: {
        background: openMenu ? theme.palette.gray[100] : 'inherit',
        padding: '4px'
      },
      children: /*#__PURE__*/_jsx(Avatar, _extends({}, AvatarProps))
    })), (triggerType === 'Button' || triggerType === 'Icon') && /*#__PURE__*/_jsx(Button, _extends({
      disabled: disabled,
      endIcon: triggerType === 'Button' && (!openMenu ? _Icon || (_Icon = /*#__PURE__*/_jsx(Icon, {
        iconId: "ChevronDown",
        size: 20
      })) : _Icon2 || (_Icon2 = /*#__PURE__*/_jsx(Icon, {
        iconId: "ChevronUp",
        size: 20
      }))),
      onClick: handleMenuOpen,
      ref: anchorEl,
      sx: _extends({
        backgroundColor: openMenu ? getButtonBgHoverColor(variant) : undefined
      }, otherMenuTriggerProps.sx),
      variant: variant
    }, otherMenuTriggerProps, {
      children: triggerType === 'Button' ? buttonText : icon
    })), /*#__PURE__*/_jsx(Popper, _extends({
      anchorEl: controlledAnchorEl != null ? controlledAnchorEl : anchorEl.current,
      disablePortal: true,
      open: openMenu,
      placement: "bottom-start",
      role: undefined,
      style: {
        margin: 0,
        zIndex: 2
      },
      transition: true
    }, popperProps, {
      "data-element": "Popper",
      "data-source-file": "DropdownMenu.tsx",
      children: function children(_ref) {
        var TransitionProps = _ref.TransitionProps;
        return /*#__PURE__*/_jsx(Fade, _extends({}, TransitionProps, {
          style: {
            transformOrigin: 'top-center'
          },
          children: /*#__PURE__*/_jsx(Paper, _extends({
            sx: _extends({
              '& .MuiMenu-list': {
                padding: 0
              },
              border: "1px solid ".concat(theme.palette.gray[200]),
              borderRadius: '8px',
              boxShadow: theme.shadows[SHADOWS.lg],
              marginTop: '5px',
              maxWidth: menuMaxWidth,
              minWidth: '240px'
            }, paperSx)
          }, otherPaperProps, {
            children: /*#__PURE__*/_jsx(ClickAwayListener, {
              onClickAway: handleMenuClose,
              children: /*#__PURE__*/_jsxs(Box, {
                className: "menu-outer-container",
                sx: _extends({}, menuMaxHeight && {
                  maxHeight: menuMaxHeight,
                  overflowY: 'scroll'
                }),
                children: [menuHeaderProps && /*#__PURE__*/_jsx(MenuHeader, _extends({
                  bottomMargin: true
                }, menuHeaderProps)), /*#__PURE__*/_jsx(DropDownMenuItems, {
                  handleMenuClose: handleMenuClose,
                  hasPrimaryMenuHeader: Boolean(menuHeaderProps),
                  menuItemProps: menuItemProps
                })]
              })
            })
          }))
        }));
      }
    }))]
  });
}
var getButtonBgHoverColor = function getButtonBgHoverColor(variant) {
  if (!variant) return undefined;
  if (variant.includes('link')) return 'none';
  switch (variant) {
    case 'primary':
      return PRIMARY700;
    case 'primary-destructive':
      return ERROR700;
    case 'secondary':
      return PRIMARY100;
    case 'secondary-destructive':
      return ERROR100;
    case 'secondary-gray':
      return GRAY100;
    case 'secondary-gray-destructive':
      return ERROR100;
    case 'tertiary':
      return PRIMARY50;
    case 'tertiary-destructive':
      return ERROR50;
    case 'tertiary-gray':
      return GRAY100;
    case 'tertiary-gray-destructive':
      return ERROR50;
    default:
      return undefined;
  }
};